import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';

function Results() {
  const { id } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [expertFeedback, setExpertFeedback] = useState({});

  const getExpectedTime = (topic) => {
    const standards = { 'Reading': 80, 'Mathematical Reasoning': 69, 'Thinking Skills': 60, 'Writing': 1800 };
    return standards[topic] || 60;
  };

  const getPacingFeedback = (timeSpent, expectedTime) => {
    if (!timeSpent || timeSpent === 0) return { status: 'unknown', color: '#9ca3af', text: 'No timing data', icon: '⏱️' };
    const ratio = timeSpent / expectedTime;
    if (ratio <= 1.0) return { status: 'good', color: '#10b981', text: 'Good pace', icon: '✓' };
    else if (ratio <= 1.5) return { status: 'moderate', color: '#f59e0b', text: 'Slightly slow', icon: '⚠️' };
    else return { status: 'slow', color: '#ef4444', text: 'Too slow', icon: '⏰' };
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate NSW-style scaled score (out of 30)
  const calculateNSWScaledScore = (rawScore, totalQuestions) => {
    if (!totalQuestions || totalQuestions === 0) return 0;
    const scaledScore = (rawScore / totalQuestions) * 30;
    return Math.round(scaledScore * 10) / 10; // Round to 1 decimal place
  };

  const getExpertFeedback = async (questionIndex) => {
    if (!result) return; // Guard clause
    const question = result.questions[questionIndex];
    
    // Don't re-score if already scored (prevents score changes and duplicate emails)
    if (question.type === 'written' && question.aiScore !== undefined) {
      console.log(`Question ${questionIndex + 1} already scored (${question.aiScore}), showing cached feedback`);
      // Just show the feedback without re-scoring
      setLoadingFeedback(prev => ({ ...prev, [questionIndex]: true }));
      try {
        const response = await apiClient.post('/writing-feedback', {
          prompt: question.questionText,
          writingText: question.userAnswer
        });
        const { feedback } = response.data;
        setExpertFeedback(prev => ({ ...prev, [questionIndex]: feedback }));
      } catch (err) {
        console.error('Error getting feedback:', err);
        setExpertFeedback(prev => ({ ...prev, [questionIndex]: 'Unable to get feedback at this time.' }));
      } finally {
        setLoadingFeedback(prev => ({ ...prev, [questionIndex]: false }));
      }
      return;
    }
    
    console.log('Question for feedback:', question); // Debug log
    console.log('Question type:', question.type); // Debug log
    setLoadingFeedback(prev => ({ ...prev, [questionIndex]: true }));
    try {
      // Check if this is a writing question by type
      const isWritingQuestion = question.type === 'written';
      console.log('Is writing question?', isWritingQuestion); // Debug log
      
      if (isWritingQuestion) {
        // Use writing-feedback endpoint for writing questions
        console.log('Using writing-feedback endpoint'); // Debug log
        const response = await apiClient.post('/writing-feedback', {
          prompt: question.questionText,
          writingText: question.userAnswer
        });
        
        const { feedback, score } = response.data;
        setExpertFeedback(prev => ({ ...prev, [questionIndex]: feedback }));
        
        // Update the question score in the database
        if (score !== undefined) {
          try {
            const updateResponse = await apiClient.post('/update-question-score', {
              resultId: id,
              questionIndex: questionIndex,
              score: score
            });
            
            // Update local result state with new score
            if (updateResponse.data.success) {
              setResult(prevResult => {
                const updatedQuestions = [...prevResult.questions];
                updatedQuestions[questionIndex] = {
                  ...updatedQuestions[questionIndex],
                  isCorrect: score >= 70,
                  aiScore: score
                };
                
                return {
                  ...prevResult,
                  questions: updatedQuestions,
                  score: updateResponse.data.newScore
                };
              });
            }
          } catch (updateErr) {
            console.error('Error updating question score:', updateErr);
            // Continue even if update fails - user still sees feedback
          }
        }
      } else {
        // Use regular feedback endpoint for MCQ
        console.log('Using regular feedback endpoint'); // Debug log
        const response = await apiClient.post('/feedback', {
          questionText: question.questionText,
          userAnswer: question.userAnswer,
          correctAnswer: question.correctAnswer,
          isCorrect: question.isCorrect
        });
        setExpertFeedback(prev => ({ ...prev, [questionIndex]: response.data.feedback }));
      }
    } catch (err) {
      console.error('Error getting expert feedback:', err);
      setExpertFeedback(prev => ({ ...prev, [questionIndex]: 'Unable to get feedback at this time.' }));
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [questionIndex]: false }));
    }
  };

  useEffect(() => {
    apiClient.get(`/result/${id}`)
      .then(response => { 
        setResult(response.data); 
        setLoading(false);
        
        // Auto-trigger AI scoring for writing questions
        const hasWritingQuestions = response.data.questions.some(q => q.type === 'written');
        if (hasWritingQuestions) {
          console.log('Auto-scoring writing questions...');
          // Score all writing questions automatically
          response.data.questions.forEach((question, index) => {
            if (question.type === 'written' && question.aiScore === undefined) {
              console.log(`Scheduling auto-score for question ${index + 1}`);
              // Trigger scoring for this question after a delay
              setTimeout(() => {
                // Re-fetch to ensure we have the latest result state
                setResult(currentResult => {
                  if (currentResult) {
                    scoreWritingQuestion(index, currentResult);
                  }
                  return currentResult;
                });
              }, index * 2000); // Stagger requests by 2 seconds
            }
          });
        }
      })
      .catch(err => { console.error('Error loading results:', err); setLoading(false); });
  }, [id]);

  // Separate function for auto-scoring to avoid closure issues
  const scoreWritingQuestion = async (questionIndex, currentResult) => {
    const question = currentResult.questions[questionIndex];
    console.log(`Auto-scoring question ${questionIndex + 1}:`, question.questionText.substring(0, 50));
    
    setLoadingFeedback(prev => ({ ...prev, [questionIndex]: true }));
    
    try {
      const response = await apiClient.post('/writing-feedback', {
        prompt: question.questionText,
        writingText: question.userAnswer
      });
      
      const { feedback, score } = response.data;
      console.log(`Question ${questionIndex + 1} scored: ${score}`);
      setExpertFeedback(prev => ({ ...prev, [questionIndex]: feedback }));
      
      if (score !== undefined) {
        const updateResponse = await apiClient.post('/update-question-score', {
          resultId: id,
          questionIndex: questionIndex,
          score: score
        });
        
        if (updateResponse.data.success) {
          setResult(prevResult => {
            const updatedQuestions = [...prevResult.questions];
            updatedQuestions[questionIndex] = {
              ...updatedQuestions[questionIndex],
              isCorrect: score >= 70,
              aiScore: score
            };
            
            return {
              ...prevResult,
              questions: updatedQuestions,
              score: updateResponse.data.newScore
            };
          });
        }
      }
    } catch (err) {
      console.error(`Error auto-scoring question ${questionIndex + 1}:`, err);
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [questionIndex]: false }));
    }
  };

  if (loading) return (<div style={{ textAlign: 'center', padding: '60px 20px' }}><div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div><p style={{ fontSize: '18px', color: '#666' }}>Loading results...</p></div>);
  if (!result) return (<div className="card" style={{ textAlign: 'center', padding: '40px' }}><div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div><h2>Results not found</h2></div>);

  // Calculate percentage based on question types
  let percentage;
  const hasWritingQuestions = result.questions.some(q => q.type === 'written');
  
  if (hasWritingQuestions && result.questions.every(q => q.type === 'written')) {
    // All writing questions - use average AI score
    const scoredQuestions = result.questions.filter(q => q.aiScore !== undefined);
    if (scoredQuestions.length > 0) {
      const totalAiScore = scoredQuestions.reduce((sum, q) => sum + q.aiScore, 0);
      percentage = Math.round(totalAiScore / scoredQuestions.length);
    } else {
      // No AI scores yet, use traditional calculation
      percentage = Math.round((result.score / result.totalQuestions) * 100);
    }
  } else if (hasWritingQuestions) {
    // Mixed questions - calculate weighted average
    let totalScore = 0;
    result.questions.forEach(q => {
      if (q.type === 'written' && q.aiScore !== undefined) {
        totalScore += q.aiScore;
      } else if (q.type !== 'written') {
        totalScore += q.isCorrect ? 100 : 0;
      }
    });
    percentage = Math.round(totalScore / result.totalQuestions);
  } else {
    // No writing questions - traditional calculation
    percentage = Math.round((result.score / result.totalQuestions) * 100);
  }

  // Calculate NSW scaled score - use percentage for writing questions
  const nswScaledScore = calculateNSWScaledScore(percentage, 100);

  const currentQuestion = result.questions[selectedQuestion];
  const expectedTime = getExpectedTime(result.topic);
  const pacingInfo = getPacingFeedback(currentQuestion.timeSpent, expectedTime);

  const isReadingTest = result.topic === 'Reading' || result.topic === 'OC_Reading';
  let passageText = null;
  let questionTextOnly = currentQuestion.questionText;
  
  if (isReadingTest) {
    const parts = currentQuestion.questionText.split('\n\n');
    if (parts.length > 1) {
      if (currentQuestion.questionText.match(/\[Same (?:passage|poem|texts) as Question \d+\]/i)) {
        const referenceMatch = currentQuestion.questionText.match(/\[Same (?:passage|poem|texts) as Question (\d+)\]/i);
        if (referenceMatch) {
          const refQuestionNum = parseInt(referenceMatch[1]) - 1;
          if (result.questions[refQuestionNum]) {
            const refQuestion = result.questions[refQuestionNum];
            const refParts = refQuestion.questionText.split('\n\n');
            if (refParts.length > 1) {
              passageText = refParts.slice(0, -1).join('\n\n');
              questionTextOnly = parts[parts.length - 1];
            }
          }
        }
        questionTextOnly = currentQuestion.questionText.replace(/\[Same (?:passage|poem|texts) as Question \d+\]\s*\n*/i, '').trim();
      } else {
        passageText = parts.slice(0, -1).join('\n\n');
        questionTextOnly = parts[parts.length - 1];
      }
    }
  }

  return (
    <div style={{ 
      maxWidth: '1600px', 
      margin: '0 auto', 
      padding: '20px'
    }}>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <Link to="/dashboard"><button className="btn-secondary">← Back to Dashboard</button></Link>
        <Link to="/exams"><button className="btn-secondary">Take Another Test</button></Link>
      </div>

      <div style={{ marginBottom: '20px', padding: '25px 35px', background: percentage >= 70 ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : percentage >= 50 ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', borderRadius: '12px', border: `3px solid ${percentage >= 70 ? '#10b981' : percentage >= 50 ? '#f59e0b' : '#ef4444'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>{result.examTitle}</div>
            <div style={{ fontSize: '16px', color: '#666' }}>{percentage >= 70 ? '🌟 Great job!' : percentage >= 50 ? '👍 Good effort!' : '💪 Keep practicing!'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '42px', fontWeight: 'bold', color: percentage >= 70 ? '#065f46' : percentage >= 50 ? '#92400e' : '#991b1b', lineHeight: '1' }}>{percentage}%</div>
            {hasWritingQuestions && result.questions.every(q => q.type === 'written') ? (
              <div style={{ fontSize: '14px', color: '#333', fontWeight: '600', marginTop: '5px' }}>Expert Scored</div>
            ) : (
              <div style={{ fontSize: '16px', color: '#333', fontWeight: '600', marginTop: '5px' }}>{result.score} / {result.totalQuestions}</div>
            )}
          </div>
        </div>
        
        {/* NSW Scaled Score Display */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '15px',
          paddingTop: '20px',
          borderTop: '2px solid rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.7)', 
            padding: '15px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600', textTransform: 'uppercase' }}>Raw Score</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>{result.score}/{result.totalQuestions}</div>
          </div>
          <div style={{ 
            background: 'rgba(255,255,255,0.7)', 
            padding: '15px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600', textTransform: 'uppercase' }}>Percentage</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>{percentage}%</div>
          </div>
          <div style={{ 
            background: 'rgba(79, 70, 229, 0.15)', 
            padding: '15px', 
            borderRadius: '8px',
            textAlign: 'center',
            border: '2px solid #4f46e5'
          }}>
            <div style={{ fontSize: '12px', color: '#4f46e5', marginBottom: '5px', fontWeight: '700', textTransform: 'uppercase' }}>NSW Scaled Score</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4f46e5' }}>{nswScaledScore}/30</div>
          </div>
        </div>
        
        <div style={{ 
          marginTop: '15px', 
          fontSize: '13px', 
          color: '#666', 
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          💡 NSW Scaled Score shows your performance on a 0-30 scale, matching the actual NSW Selective Test format
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '20px' }}>
        <div>
          <div className="card" style={{ position: 'sticky', top: '20px', padding: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#4f46e5' }}>📝 Questions</h3>
            <div style={{ marginBottom: '15px', padding: '10px', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '16px', height: '16px', background: '#10b981', borderRadius: '4px' }}></div><span>Correct</span></div>
              <div style={{ fontSize: '12px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '16px', height: '16px', background: '#ef4444', borderRadius: '4px' }}></div><span>Incorrect</span></div>
              <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '16px', height: '16px', background: '#9ca3af', borderRadius: '4px' }}></div><span>Skipped</span></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {result.questions.map((q, idx) => {
                const isAnswered = q.userAnswer && q.userAnswer !== '';
                const bgColor = !isAnswered ? '#9ca3af' : q.isCorrect ? '#10b981' : '#ef4444';
                const isSelected = idx === selectedQuestion;
                const qTime = q.timeSpent || 0;
                return (
                  <div key={idx} style={{ position: 'relative' }}>
                    <button onClick={() => setSelectedQuestion(idx)} style={{ width: '100%', padding: '10px 6px', background: bgColor, color: 'white', border: isSelected ? '3px solid #4f46e5' : 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', transform: isSelected ? 'scale(1.1)' : 'scale(1)', boxShadow: isSelected ? '0 4px 12px rgba(79, 70, 229, 0.4)' : 'none' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.transform = 'scale(1.05)'; }} onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.transform = 'scale(1)'; }}>{idx + 1}</button>
                    {qTime > 0 && <div style={{ fontSize: '10px', color: '#666', textAlign: 'center', marginTop: '3px', fontWeight: '500' }}>{formatTime(qTime)}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          {passageText ? (
            <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ display: 'flex', minHeight: '600px' }}>
                <div style={{ flex: '0 0 45%', padding: '35px 30px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRight: '3px solid #f59e0b', overflowY: 'auto', maxHeight: '85vh', position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
                  <div style={{ background: '#f59e0b', color: 'white', padding: '10px 18px', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', marginBottom: '20px', display: 'inline-block' }}>📖 Reading Passage</div>
                  <div style={{ fontSize: '18px', lineHeight: '2.0', color: '#1f2937', whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif' }}>{passageText}</div>
                </div>
                <div style={{ flex: '1', padding: '35px', overflowY: 'auto' }}>
                  <div style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '3px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h2 style={{ fontSize: '28px', color: '#4f46e5', fontWeight: 'bold' }}>Question {selectedQuestion + 1}</h2>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                        <span style={{ padding: '12px 24px', borderRadius: '25px', fontSize: '18px', fontWeight: 'bold', background: currentQuestion.isCorrect ? '#d1fae5' : '#fee2e2', color: currentQuestion.isCorrect ? '#065f46' : '#991b1b' }}>{currentQuestion.isCorrect ? '✓ Correct' : '✗ Incorrect'}</span>
                        {currentQuestion.aiScore !== undefined && (
                          <span style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>AI Score: {currentQuestion.aiScore}/100</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', background: '#f9fafb', padding: '15px 20px', borderRadius: '12px' }}>
                      <div style={{ flex: 1 }}><div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Time Spent</div><div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4f46e5' }}>{formatTime(currentQuestion.timeSpent)}</div></div>
                      <div style={{ flex: 1 }}><div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Pacing</div><div style={{ fontSize: '18px', fontWeight: 'bold', color: pacingInfo.color, display: 'flex', alignItems: 'center', gap: '8px' }}><span>{pacingInfo.icon}</span><span>{pacingInfo.text}</span></div></div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '30px' }}><h3 style={{ fontSize: '20px', marginBottom: '20px', lineHeight: '1.8', color: '#1f2937', fontWeight: '600' }}>{questionTextOnly}</h3></div>
                  
                  {/* Display user's written answer for writing questions */}
                  {currentQuestion.type === 'written' && currentQuestion.userAnswer && (
                    <div style={{ marginBottom: '30px', background: '#f0f9ff', padding: '25px', borderRadius: '14px', border: '3px solid #3b82f6' }}>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>✍️</span>
                        <span>Your Writing:</span>
                      </h4>
                      <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#1f2937', whiteSpace: 'pre-wrap', background: 'white', padding: '20px', borderRadius: '10px', border: '2px solid #bfdbfe', fontFamily: 'Georgia, serif' }}>
                        {currentQuestion.userAnswer}
                      </div>
                      <div style={{ marginTop: '12px', fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>
                        Word count: {currentQuestion.userAnswer.trim().split(/\s+/).length} words
                      </div>
                    </div>
                  )}
                  
                  {/* Display all answer options */}
                  {currentQuestion.options && currentQuestion.options.length > 0 && (
                    <div style={{ marginBottom: '30px', background: '#f9fafb', padding: '25px', borderRadius: '14px', border: '2px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#4f46e5', margin: 0 }}>Answer Options:</h4>
                        <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                          <span style={{ color: '#10b981', fontWeight: '600' }}>✓ Green</span> = Correct answer • <span style={{ color: '#ef4444', fontWeight: '600' }}>✗ Red</span> = Your incorrect answer
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {currentQuestion.options.map((option) => {
                          const isUserAnswer = currentQuestion.userAnswer === option.id || currentQuestion.userAnswer === option.id.toUpperCase();
                          const isCorrectAnswer = currentQuestion.correctAnswer === option.id || currentQuestion.correctAnswer === option.id.toUpperCase();
                          let borderColor = '#d1d5db';
                          let bgColor = 'white';
                          let textColor = '#1f2937';
                          
                          if (isCorrectAnswer) {
                            borderColor = '#10b981';
                            bgColor = '#d1fae5';
                            textColor = '#065f46';
                          } else if (isUserAnswer && !currentQuestion.isCorrect) {
                            borderColor = '#ef4444';
                            bgColor = '#fee2e2';
                            textColor = '#991b1b';
                          }
                          
                          return (
                            <div key={option.id} style={{ 
                              padding: '16px 20px', 
                              background: bgColor, 
                              border: `3px solid ${borderColor}`, 
                              borderRadius: '10px',
                              fontSize: '18px',
                              fontWeight: isCorrectAnswer || isUserAnswer ? '600' : '500',
                              color: textColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{ 
                                fontWeight: 'bold', 
                                fontSize: '16px',
                                minWidth: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isCorrectAnswer ? '#10b981' : isUserAnswer ? '#ef4444' : '#9ca3af',
                                color: 'white',
                                borderRadius: '6px'
                              }}>
                                {option.id.toUpperCase()}
                              </span>
                              <span style={{ flex: 1 }}>{option.text}</span>
                              {isCorrectAnswer && <span style={{ fontSize: '20px' }}>✓</span>}
                              {isUserAnswer && !currentQuestion.isCorrect && <span style={{ fontSize: '20px' }}>✗</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: '28px', borderRadius: '16px', border: '3px solid #f59e0b', marginBottom: '30px', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '24px', color: '#92400e', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '12px' }}><span style={{ fontSize: '32px' }}>💡</span><span>Expert Hint</span></h4>
                      {!expertFeedback[selectedQuestion] && (<button onClick={() => getExpertFeedback(selectedQuestion)} disabled={loadingFeedback[selectedQuestion]} className="btn-primary" style={{ padding: '12px 24px', fontSize: '17px', fontWeight: 'bold', opacity: loadingFeedback[selectedQuestion] ? 0.6 : 1 }}>{loadingFeedback[selectedQuestion] ? '⏳ Loading...' : 'Show Hint'}</button>)}
                    </div>
                    {expertFeedback[selectedQuestion] && (<p style={{ fontSize: '19px', lineHeight: '1.9', color: '#78350f', whiteSpace: 'pre-wrap', background: 'white', padding: '22px', borderRadius: '12px', border: '2px solid #fbbf24', fontWeight: '500' }}>{expertFeedback[selectedQuestion]}</p>)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '25px', borderTop: '3px solid #e5e7eb' }}>
                    <button onClick={() => setSelectedQuestion(Math.max(0, selectedQuestion - 1))} disabled={selectedQuestion === 0} className="btn-secondary" style={{ padding: '14px 28px', fontSize: '17px', fontWeight: 'bold', opacity: selectedQuestion === 0 ? 0.4 : 1, cursor: selectedQuestion === 0 ? 'not-allowed' : 'pointer' }}>← Previous</button>
                    <button onClick={() => setSelectedQuestion(Math.min(result.totalQuestions - 1, selectedQuestion + 1))} disabled={selectedQuestion === result.totalQuestions - 1} className="btn-primary" style={{ padding: '14px 28px', fontSize: '17px', fontWeight: 'bold', opacity: selectedQuestion === result.totalQuestions - 1 ? 0.4 : 1, cursor: selectedQuestion === result.totalQuestions - 1 ? 'not-allowed' : 'pointer' }}>Next →</button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: '35px' }}>
              <div style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '3px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h2 style={{ fontSize: '28px', color: '#4f46e5', fontWeight: 'bold' }}>Question {selectedQuestion + 1}</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <span style={{ padding: '12px 24px', borderRadius: '25px', fontSize: '18px', fontWeight: 'bold', background: currentQuestion.isCorrect ? '#d1fae5' : '#fee2e2', color: currentQuestion.isCorrect ? '#065f46' : '#991b1b' }}>{currentQuestion.isCorrect ? '✓ Correct' : '✗ Incorrect'}</span>
                    {currentQuestion.aiScore !== undefined && (
                      <span style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>AI Score: {currentQuestion.aiScore}/100</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', background: '#f9fafb', padding: '15px 20px', borderRadius: '12px' }}>
                  <div style={{ flex: 1 }}><div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Time Spent</div><div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4f46e5' }}>{formatTime(currentQuestion.timeSpent)}</div></div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Pacing</div><div style={{ fontSize: '18px', fontWeight: 'bold', color: pacingInfo.color, display: 'flex', alignItems: 'center', gap: '8px' }}><span>{pacingInfo.icon}</span><span>{pacingInfo.text}</span></div></div>
                </div>
              </div>
              <div style={{ marginBottom: '30px' }}><h3 style={{ fontSize: '20px', marginBottom: '20px', lineHeight: '1.8', color: '#1f2937', fontWeight: '600' }}>{questionTextOnly}</h3></div>
              {currentQuestion.imageSvg && (<div style={{ margin: '30px 0', padding: '30px', background: '#f9fafb', borderRadius: '12px', border: '2px solid #e5e7eb', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><div dangerouslySetInnerHTML={{ __html: currentQuestion.imageSvg }} style={{ maxWidth: '100%' }} /></div>)}
              
              {/* Display user's written answer for writing questions */}
              {currentQuestion.type === 'written' && currentQuestion.userAnswer && (
                <div style={{ marginBottom: '30px', background: '#f0f9ff', padding: '25px', borderRadius: '14px', border: '3px solid #3b82f6' }}>
                  <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '24px' }}>✍️</span>
                    <span>Your Writing:</span>
                  </h4>
                  <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#1f2937', whiteSpace: 'pre-wrap', background: 'white', padding: '20px', borderRadius: '10px', border: '2px solid #bfdbfe', fontFamily: 'Georgia, serif' }}>
                    {currentQuestion.userAnswer}
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>
                    Word count: {currentQuestion.userAnswer.trim().split(/\s+/).length} words
                  </div>
                </div>
              )}
              
              {/* Display all answer options */}
              {currentQuestion.options && currentQuestion.options.length > 0 && (
                <div style={{ marginBottom: '30px', background: '#f9fafb', padding: '25px', borderRadius: '14px', border: '2px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#4f46e5', margin: 0 }}>Answer Options:</h4>
                    <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                      <span style={{ color: '#10b981', fontWeight: '600' }}>✓ Green</span> = Correct answer • <span style={{ color: '#ef4444', fontWeight: '600' }}>✗ Red</span> = Your incorrect answer
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {currentQuestion.options.map((option) => {
                      const isUserAnswer = currentQuestion.userAnswer === option.id || currentQuestion.userAnswer === option.id.toUpperCase();
                      const isCorrectAnswer = currentQuestion.correctAnswer === option.id || currentQuestion.correctAnswer === option.id.toUpperCase();
                      let borderColor = '#d1d5db';
                      let bgColor = 'white';
                      let textColor = '#1f2937';
                      
                      if (isCorrectAnswer) {
                        borderColor = '#10b981';
                        bgColor = '#d1fae5';
                        textColor = '#065f46';
                      } else if (isUserAnswer && !currentQuestion.isCorrect) {
                        borderColor = '#ef4444';
                        bgColor = '#fee2e2';
                        textColor = '#991b1b';
                      }
                      
                      return (
                        <div key={option.id} style={{ 
                          padding: '16px 20px', 
                          background: bgColor, 
                          border: `3px solid ${borderColor}`, 
                          borderRadius: '10px',
                          fontSize: '18px',
                          fontWeight: isCorrectAnswer || isUserAnswer ? '600' : '500',
                          color: textColor,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ 
                            fontWeight: 'bold', 
                            fontSize: '16px',
                            minWidth: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isCorrectAnswer ? '#10b981' : isUserAnswer ? '#ef4444' : '#9ca3af',
                            color: 'white',
                            borderRadius: '6px'
                          }}>
                            {option.id.toUpperCase()}
                          </span>
                          <span style={{ flex: 1 }}>{option.text}</span>
                          {isCorrectAnswer && <span style={{ fontSize: '20px' }}>✓</span>}
                          {isUserAnswer && !currentQuestion.isCorrect && <span style={{ fontSize: '20px' }}>✗</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: '28px', borderRadius: '16px', border: '3px solid #f59e0b', marginBottom: '30px', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '24px', color: '#92400e', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '12px' }}><span style={{ fontSize: '32px' }}>💡</span><span>Expert Hint</span></h4>
                  {!expertFeedback[selectedQuestion] && (<button onClick={() => getExpertFeedback(selectedQuestion)} disabled={loadingFeedback[selectedQuestion]} className="btn-primary" style={{ padding: '12px 24px', fontSize: '17px', fontWeight: 'bold', opacity: loadingFeedback[selectedQuestion] ? 0.6 : 1 }}>{loadingFeedback[selectedQuestion] ? '⏳ Loading...' : 'Show Hint'}</button>)}
                </div>
                {expertFeedback[selectedQuestion] && (<p style={{ fontSize: '19px', lineHeight: '1.9', color: '#78350f', whiteSpace: 'pre-wrap', background: 'white', padding: '22px', borderRadius: '12px', border: '2px solid #fbbf24', fontWeight: '500' }}>{expertFeedback[selectedQuestion]}</p>)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '25px', borderTop: '3px solid #e5e7eb' }}>
                <button onClick={() => setSelectedQuestion(Math.max(0, selectedQuestion - 1))} disabled={selectedQuestion === 0} className="btn-secondary" style={{ padding: '14px 28px', fontSize: '17px', fontWeight: 'bold', opacity: selectedQuestion === 0 ? 0.4 : 1, cursor: selectedQuestion === 0 ? 'not-allowed' : 'pointer' }}>← Previous</button>
                <button onClick={() => setSelectedQuestion(Math.min(result.totalQuestions - 1, selectedQuestion + 1))} disabled={selectedQuestion === result.totalQuestions - 1} className="btn-primary" style={{ padding: '14px 28px', fontSize: '17px', fontWeight: 'bold', opacity: selectedQuestion === result.totalQuestions - 1 ? 0.4 : 1, cursor: selectedQuestion === result.totalQuestions - 1 ? 'not-allowed' : 'pointer' }}>Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Results;
